import { useState, useRef, useEffect } from 'react'
import { useLocation, useNavigate, Link } from '@tanstack/react-router'
import { Menu, LogOut, User, Globe } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '@/application/stores/auth.store'
import { useSidebarStore } from '@/application/stores/sidebar.store'
import { useGetMe } from '@/application/services/user.service'
import { UserAvatar } from '@/ui/components/common/UserAvatar'
import { classNames } from '@/ui/utils/main'
import { PublicThemeToggle } from '@/ui/components/public/PublicThemeToggle'

const LANGUAGES = [
  { code: 'fr', label: 'FR', flag: '🇫🇷' },
  { code: 'en', label: 'EN', flag: '🇬🇧' },
  { code: 'es', label: 'ES', flag: '🇪🇸' },
] as const

export function Header() {
  const location = useLocation()
  const navigate = useNavigate()
  const { user, logout } = useAuthStore()
  const { t, i18n } = useTranslation()
  const toggle = useSidebarStore((state) => state.toggle)
  const { data: profile } = useGetMe()

  const [avatarOpen, setAvatarOpen] = useState(false)
  const [langOpen, setLangOpen] = useState(false)
  const avatarRef = useRef<HTMLDivElement>(null)
  const langRef = useRef<HTMLDivElement>(null)

  const currentLang = LANGUAGES.find((lang) => lang.code === i18n.language) ?? LANGUAGES[0]

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        avatarRef.current &&
        event.target instanceof Node &&
        !avatarRef.current.contains(event.target)
      ) {
        setAvatarOpen(false)
      }
      if (
        langRef.current &&
        event.target instanceof Node &&
        !langRef.current.contains(event.target)
      ) {
        setLangOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const getPageTitle = (pathname: string): string => {
    const path = pathname.replace(/^\/manage\//, '')
    if (!path || path === 'dashboard') return t('admin.header_title_dashboard')
    if (path === 'profile') return t('admin.profile_title')
    if (path === 'live') return t('admin.live_title')
    if (path === 'settings') return t('admin.settings_title')
    if (path === 'investigations') return t('admin.header_title_investigations')
    if (path === 'investigations/create') return t('admin.header_title_new_investigation')
    if (/^investigations\/.+\/edit$/.test(path)) return t('admin.header_title_edit_investigation')
    if (/^investigations\/.+\/leads\/.+$/.test(path)) return t('admin.header_title_lead_detail')
    if (/^investigations\/.+\/leads$/.test(path)) return t('admin.header_title_investigation_leads')
    if (path === 'categories') return t('categories.page_title')
    if (path === 'investigators') return t('admin.investigators_page_title')
    if (path === 'comments' || /^comments\/.+$/.test(path)) return t('admin.nav_comments')
    if (path === 'import-export') return t('admin.nav_import_export')
    if (path === 'translations') return t('translations.dashboard_title')
    if (/^translations\/.+\/leads\/.+$/.test(path)) return t('translations.detail_lead_title')
    if (/^translations\/.+$/.test(path)) return t('translations.detail_investigation_title')
    return path
      .split('/')
      .filter((segment) => !/^[0-9a-f]{8}-/.test(segment)) // filter out UUIDs
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }

  const getPageSubtitle = (pathname: string): string | null => {
    const path = pathname.replace(/^\/manage\//, '')
    if (path === 'profile') return t('admin.profile_subtitle')
    if (path === 'live') return t('admin.live_subtitle')
    if (path === 'settings') return t('admin.settings_subtitle', { appName: t('app_name') })
    if (path === 'investigations') return t('admin.inv_page_subtitle')
    if (path === 'investigations/create') return t('admin.form_create_subtitle')
    if (/^investigations\/.+\/edit$/.test(path)) return t('admin.form_edit_subtitle')
    if (path === 'categories') return t('categories.page_subtitle')
    if (path === 'investigators') return t('admin.investigators_page_subtitle')
    if (path === 'comments' || /^comments\/.+$/.test(path)) return t('admin.comments_subtitle')
    if (path === 'import-export') return t('admin.import_export_subtitle')
    if (path === 'translations') return t('translations.dashboard_subtitle')
    if (/^translations\/.+\/leads\/.+$/.test(path)) return t('translations.detail_lead_subtitle')
    if (/^translations\/.+$/.test(path)) return t('translations.detail_investigation_subtitle')
    return null
  }

  const handleLogout = () => {
    setAvatarOpen(false)
    logout()
    navigate({ to: '/login' })
  }

  /*
   * Root classes, in the order they matter:
   *
   * `sticky top-0` — same contract as the public navbar. The document itself
   * stays the scroller (ManagementLayout deliberately has no inner
   * `overflow-y-auto` region, see the comments there), so the header travels
   * the whole page and stays in view. Its parent chain is
   * `flex min-h-screen-safe > flex-1 flex flex-col`, and that column stretches
   * to the full height of the flex row, so the header has the entire document
   * height to travel across rather than a navbar-height box.
   *
   * `h-header-safe pt-safe-top` — one unit, not two independent choices.
   * Installed as a PWA the iOS window is standalone and `viewport-fit=cover`
   * lets it paint under the status bar. The body deliberately does NOT pad the
   * top inset (see common.css): body padding lives outside the scrolling
   * content, so a `sticky top-0` header would pin to the padding edge and
   * visibly slide up by the inset before catching. The header therefore owns
   * that inset itself, and `h-header-safe` grows its box by exactly the same
   * amount so the 4rem row keeps its full height — `h-16` alone with
   * `pt-safe-top` would squash the title and avatar into less than 64px.
   * Both resolve to 0px / `h-16` in a normal browser tab.
   *
   * `bg-background` must stay opaque: the header now scrolls over
   * `bg-muted/40` content, and it is also what fills the status-bar strip the
   * top inset opens up.
   *
   * `z-30` is deliberately BELOW the mobile sidebar drawer (`z-50`) and its
   * backdrop (`z-40`): the drawer has to overlay the header, and the backdrop
   * has to dim it along with the rest of the page. Matching z-indexes would
   * not be enough — the header comes after the sidebar in the DOM and would
   * win the tie. The header's own dropdowns are `absolute z-50` inside the
   * stacking context this z-index creates, so they keep painting above page
   * content while the header as a whole stays under the drawer.
   */
  return (
    <header className="sticky top-0 z-30 h-header-safe pt-safe-top border-b border-border px-6 flex items-center justify-between bg-background">
      <div className="flex items-center gap-4">
        {/* Mobile menu toggle */}
        <button
          onClick={toggle}
          className="lg:hidden p-2 rounded-lg hover:bg-accent hover:text-accent-foreground transition-colors"
          aria-label="Menu"
        >
          <Menu className="w-5 h-5" />
        </button>

        {/* Page title */}
        <div className="flex flex-col">
          <h1 className="text-xl font-bold">{getPageTitle(location.pathname)}</h1>
          {getPageSubtitle(location.pathname) && (
            <p className="text-sm text-muted-foreground hidden lg:block">
              {getPageSubtitle(location.pathname)}
            </p>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-4">
        {/* Language selector */}
        <div className="relative" ref={langRef}>
          <button
            onClick={() => setLangOpen((prev) => !prev)}
            className="flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors px-2 py-1 rounded border border-border hover:border-foreground/30"
          >
            <Globe className="h-3 w-3" />
            {currentLang.label}
          </button>
          {langOpen && (
            <div className="absolute right-0 top-full mt-1 w-28 rounded-lg border border-border bg-background shadow-lg z-50 overflow-hidden">
              {LANGUAGES.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => {
                    void i18n.changeLanguage(lang.code)
                    setLangOpen(false)
                  }}
                  className={classNames(
                    'w-full flex items-center gap-2 px-3 py-2 text-xs hover:bg-accent hover:text-accent-foreground transition-colors text-left',
                    i18n.language === lang.code
                      ? 'font-semibold text-foreground'
                      : 'text-muted-foreground',
                  )}
                >
                  <span>{lang.flag}</span>
                  {lang.label}
                </button>
              ))}
            </div>
          )}
        </div>

        <PublicThemeToggle />

        {/* User avatar with dropdown */}
        {user && (
          <div className="relative" ref={avatarRef}>
            <button
              onClick={() => setAvatarOpen((prev) => !prev)}
              className="hover:opacity-90 transition-opacity rounded-full"
              title={user.displayName || user.username}
            >
              <UserAvatar
                displayName={user.displayName}
                username={user.username}
                avatarUrl={profile?.avatarUrl}
              />
            </button>

            {avatarOpen && (
              <div className="absolute right-0 top-full mt-2 w-48 rounded-lg border border-border bg-background shadow-lg z-50 overflow-hidden">
                <div className="px-4 py-3 border-b border-border">
                  <p className="font-medium text-sm">{user.displayName || user.username}</p>
                  <p className="text-xs text-muted-foreground capitalize">{user.role}</p>
                </div>
                <Link
                  to="/manage/profile"
                  onClick={() => setAvatarOpen(false)}
                  className="w-full flex items-center gap-2 px-4 py-2 text-sm hover:bg-accent hover:text-accent-foreground transition-colors no-underline hover:no-underline text-foreground"
                >
                  <User className="w-4 h-4" />
                  {t('admin.nav_profile')}
                </Link>
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-2 px-4 py-2 text-sm text-destructive hover:bg-destructive/10 transition-colors"
                >
                  <LogOut className="w-4 h-4" />
                  {t('admin.logout')}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </header>
  )
}
